import { useState, useEffect } from 'react';

/**
 * Custom hook for detecting device performance and mobile capabilities
 * Adjusts animations and features based on device performance
 */
export const useDeviceOptimization = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSlowDevice, setIsSlowDevice] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [connectionSpeed, setConnectionSpeed] = useState('unknown');

  useEffect(() => {
    const checkDeviceCapabilities = () => {
      // Mobile detection
      const mobile = window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent);
      setIsMobile(mobile);

      // Performance detection
      const startTime = performance.now();
      requestAnimationFrame(() => {
        const endTime = performance.now();
        const frameDuration = endTime - startTime;
        
        // If frame takes longer than expected, consider it a slow device
        setIsSlowDevice(frameDuration > 20 || navigator.hardwareConcurrency < 4);
      });

      // Reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setReducedMotion(prefersReducedMotion);

      // Network speed detection
      if (navigator.connection) {
        const connection = navigator.connection;
        const effectiveType = connection.effectiveType;
        setConnectionSpeed(effectiveType);
      }
    };

    checkDeviceCapabilities();
    
    // Listen for resize events
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Listen for reduced motion changes
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionChange = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleMotionChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  // Animation settings based on device capabilities
  const getAnimationConfig = () => {
    if (reducedMotion) {
      return {
        duration: 0.1,
        scale: 1,
        enabled: false
      };
    }

    if (isSlowDevice || connectionSpeed === 'slow-2g' || connectionSpeed === '2g') {
      return {
        duration: 0.3,
        scale: 1.02,
        enabled: true,
        reduced: true
      };
    }

    return {
      duration: 0.5,
      scale: 1.05,
      enabled: true,
      reduced: false
    };
  };

  // triggerHapticFeedback (navigator.vibrate mit 50 bis 200 ms) ist seit
  // Oktober 2026 entfernt: Es hatte keinen Aufrufer. Haptik kommt aus
  // utils/haptics.js.

  return {
    isMobile,
    isSlowDevice,
    reducedMotion,
    connectionSpeed,
    animationConfig: getAnimationConfig()
  };
};

export default useDeviceOptimization;
